<template>
    <section class="shared-users">
        <h4 class="shared-users__title">{{ title }}</h4>
        <p class="shared-users__description"
           v-for="(description, index) in descriptions"
           :key="index">
            {{ description }}
        </p>
        <section class="shared-users__tags" v-if="modelValue.length > 0">
            <MultiselectPersonTag v-for="user in modelValue"
                                  :key="user.id"
                                  :option="{ value: user.id, label: fullName(user) }"
                                  :disabled="disabled || isBusy"
                                  removable
                                  @click="confirmRemove(user)" />
        </section>
        <ErrorMessage v-if="hasError && !isModalVisible" :errorMsgs="[$t('com.global.error')]" />
        <button type="button" class="shared-users__button" :disabled="disabled || isBusy" @click.prevent="openModal">
            {{ $t('com.file.share-users-modal.select-users') }}
        </button>

        <section class="esesja-modal" v-if="isModalVisible">
            <section class="esesja-modal__wraper users-picker">
                <section class="esesja-modal__wraper__header">
                    <h3 class="esesja-modal__wraper__header__title">{{ title }}</h3>
                    <button type="button"
                            class="esesja-modal__wraper__header__exit-btn"
                            :disabled="isSaving"
                            @click.prevent="isModalVisible = false">
                        <img src="@/assets/icons/components/exit-btn.svg" />
                    </button>
                </section>

                <!-- Enter w wyszukiwarce nie może zatwierdzić formularza rodzica (AddGroup / EditMeeting) -->
                <section class="users-picker__toolbar" @keydown.enter.prevent>
                    <input type="text"
                           class="users-picker__search"
                           v-model="search"
                           :placeholder="$t('com.global.search')" />
                    <select class="users-picker__group-filter" v-model="groupFilter">
                        <option value="">{{ $t('com.file.share-users-modal.all-groups') }}</option>
                        <option v-for="group in groupNames" :key="group" :value="group">{{ group }}</option>
                    </select>
                </section>

                <section class="users-picker__panels" :class="{ 'users-picker__panels--disabled': isSaving }">
                    <section class="users-picker__panel">
                        <header class="users-picker__panel-header">
                            <span>
                                {{ $t('com.file.share-users-modal.available') }}
                                <span class="users-picker__count">{{ availableUsers.length }}</span>
                            </span>
                            <button type="button"
                                    class="users-picker__bulk"
                                    :disabled="isSaving || availableUsers.length === 0"
                                    @click.prevent="addAll">
                                {{ $t('com.file.share-users-modal.add-visible') }}
                            </button>
                        </header>
                        <section class="users-picker__list">
                            <article class="users-picker__person users-picker__person--clickable"
                                     v-for="user in availableUsers"
                                     :key="user.id"
                                     @click="add(user)">
                                <span class="users-picker__avatar">
                                    <img v-if="!failedAvatars[user.id]" :src="avatarUrl(user)" alt="" @error="markAvatarFailed(user)" />
                                    <template v-else>{{ initials(user) }}</template>
                                </span>
                                <span class="users-picker__person-data">
                                    <span class="users-picker__person-name">{{ fullName(user) }}</span>
                                    <span class="users-picker__person-role" v-if="user.role">{{ user.role }}</span>
                                    <span class="users-picker__person-groups" v-if="(user.groups ?? []).length > 0">
                                        <span class="users-picker__group-chip" v-for="group in user.groups" :key="group">{{ group }}</span>
                                    </span>
                                </span>
                                <button type="button"
                                        class="users-picker__action"
                                        :disabled="isSaving"
                                        v-tooltip="$t('com.global.add')"
                                        @click.stop.prevent="add(user)">+</button>
                            </article>
                            <p class="users-picker__empty" v-if="availableUsers.length === 0">{{ $t('com.global.no-results') }}</p>
                        </section>
                    </section>

                    <section class="users-picker__panel users-picker__panel--selected">
                        <header class="users-picker__panel-header">
                            <span>
                                {{ $t('com.file.share-users-modal.selected') }}
                                <span class="users-picker__count users-picker__count--selected">{{ selectedUsers.length }}</span>
                            </span>
                            <button type="button"
                                    class="users-picker__bulk"
                                    :disabled="isSaving || selectedUsers.length === 0"
                                    @click.prevent="confirmRemoveAll">
                                {{ $t('com.file.share-users-modal.remove-all') }}
                            </button>
                        </header>
                        <section class="users-picker__list">
                            <article class="users-picker__person"
                                     v-for="user in selectedUsers"
                                     :key="user.id">
                                <span class="users-picker__avatar">
                                    <img v-if="!failedAvatars[user.id]" :src="avatarUrl(user)" alt="" @error="markAvatarFailed(user)" />
                                    <template v-else>{{ initials(user) }}</template>
                                </span>
                                <span class="users-picker__person-data">
                                    <span class="users-picker__person-name">{{ fullName(user) }}</span>
                                    <span class="users-picker__person-role" v-if="user.role">{{ user.role }}</span>
                                    <span class="users-picker__person-groups" v-if="(user.groups ?? []).length > 0">
                                        <span class="users-picker__group-chip" v-for="group in user.groups" :key="group">{{ group }}</span>
                                    </span>
                                </span>
                                <button type="button"
                                        class="users-picker__action users-picker__action--remove"
                                        :disabled="isSaving"
                                        v-tooltip="$t('com.global.delete')"
                                        @click.stop.prevent="remove(user)">&times;</button>
                            </article>
                            <p class="users-picker__empty" v-if="selectedUsers.length === 0">{{ $t('com.file.share-users-modal.none-selected') }}</p>
                        </section>
                    </section>
                </section>

                <ErrorMessage v-if="hasError" :errorMsgs="[$t('com.global.error')]" />
                <section class="esesja-modal__wraper__body__bottom users-picker__bottom" :aria-busy="isSaving">
                    <button type="button"
                            class="esesja-modal__wraper__body__bottom-button"
                            :disabled="isSaving"
                            @click.prevent="isModalVisible = false">
                        {{ $t('com.global.cancel') }}
                    </button>
                    <button type="button"
                            class="esesja-modal__wraper__body__bottom-button esesja-modal__wraper__body__bottom-button--red"
                            :disabled="disabled || isSaving"
                            @click.prevent="save">
                        {{ $t(saveSelection ? 'com.global.save' : 'com.global.confirm') }}
                    </button>
                </section>
            </section>
        </section>
    </section>
</template>

<script>
    import MultiselectPersonTag from "@/components/BasicControls/Multiselect/MultiselectPersonTag.vue";
    import MeetingService from "@/services/meetings.service";
    import ErrorMessage from "@/components/BasicControls/ErrorMessage.vue";

    // Lista osób spoza grup, którym udostępniono posiedzenie (lub domyślnie posiedzenia grupy),
    // z oknem wyboru w dwóch panelach: dostępni / wybrani. Kandydaci są pobierani raz przy otwarciu
    // okna - wyszukiwanie, filtr grup, dodawanie i usuwanie osób działają lokalnie.
    // Rodzic trzyma listę przez v-model. Opcjonalny saveSelection zapisuje ją przed
    // aktualizacją formularza; bez niego wybór jest zatwierdzany lokalnie.
    export default {
        name: "SharedUsersPicker",
        emits: ["update:modelValue", "busy"],
        props: {
            disabled: {
                type: Boolean,
                default: false,
            },
            saveSelection: {
                type: Function,
                default: null,
            },
            modelValue: {
                type: Array,
                default: () => []
            },
            // grupy, których członkowie nie są kandydatami do udostępnienia
            groupIds: {
                type: Array,
                default: () => []
            },
            title: {
                type: String,
                default: ""
            },
            descriptions: {
                type: Array,
                default: () => []
            },
        },
        components: {
            MultiselectPersonTag,
            ErrorMessage,
        },
        data() {
            return {
                candidates: [],
                selectedIds: [],
                search: "",
                groupFilter: "",
                failedAvatars: {},
                isModalVisible: false,
                isLoading: false,
                isSaving: false,
                hasError: false,
            };
        },
        computed: {
            isBusy() {
                return this.isLoading || this.isSaving || this.isModalVisible;
            },
            sortedCandidates() {
                const collator = new Intl.Collator(this.$i18n.locale, { sensitivity: "base", numeric: true });
                return [...this.candidates].sort((a, b) =>
                    collator.compare(a.firstName ?? "", b.firstName ?? "") ||
                    collator.compare(a.lastName ?? "", b.lastName ?? ""));
            },
            groupNames() {
                return [...new Set(this.candidates.flatMap((user) => user.groups ?? []))]
                    .sort((a, b) => a.localeCompare(b, this.$i18n.locale, { sensitivity: "base", numeric: true }));
            },
            selectedUsers() {
                return this.sortedCandidates.filter((user) => this.selectedIds.indexOf(user.id) !== -1);
            },
            // wyszukiwanie po imieniu i nazwisku (w obu kolejnościach), funkcji i grupach + filtr grupy
            availableUsers() {
                const phrase = this.search.trim().toLocaleLowerCase();

                return this.sortedCandidates
                    .filter((user) => this.selectedIds.indexOf(user.id) === -1)
                    .filter((user) => this.groupFilter === "" || (user.groups ?? []).indexOf(this.groupFilter) !== -1)
                    .filter((user) => phrase.length === 0 || this.searchText(user).includes(phrase));
            },
        },
        watch: {
            isBusy: {
                flush: "sync",
                handler(value) {
                    this.$emit("busy", value);
                },
            },
        },
        beforeUnmount() {
            this.$emit("busy", false);
        },
        methods: {
            fullName(user) {
                return [user.firstName, user.lastName].filter((part) => part).join(" ");
            },
            initials(user) {
                const letters = [user.firstName, user.lastName]
                    .filter((part) => part)
                    .map((part) => part.trim().charAt(0).toLocaleUpperCase())
                    .join("");

                return letters.length > 0 ? letters : "?";
            },
            searchText(user) {
                return [this.fullName(user), `${user.lastName ?? ""} ${user.firstName ?? ""}`, user.role ?? "", (user.groups ?? []).join(" ")]
                    .join(" ")
                    .toLocaleLowerCase();
            },
            avatarUrl(user) {
                return `https://ssl.esesja.pl/users/${user.id}.jpg`;
            },
            markAvatarFailed(user) {
                this.failedAvatars = { ...this.failedAvatars, [user.id]: true };
            },
            add(user) {
                if (this.isSaving) {
                    return;
                }
                if (this.selectedIds.indexOf(user.id) === -1) {
                    this.selectedIds = [...this.selectedIds, user.id];
                }
            },
            addAll() {
                if (this.isSaving) {
                    return;
                }
                const ids = this.availableUsers.map((user) => user.id);
                this.selectedIds = [...this.selectedIds, ...ids.filter((id) => this.selectedIds.indexOf(id) === -1)];
            },
            remove(user) {
                if (this.isSaving) {
                    return;
                }
                this.selectedIds = this.selectedIds.filter((id) => id !== user.id);
            },
            confirmRemoveAll() {
                if (this.isSaving) {
                    return;
                }
                this.$store.commit("showConfirmationAlert", () => {
                    if (!this.isSaving) {
                        this.selectedIds = [];
                    }
                });
            },
            async openModal() {
                if (this.disabled || this.isBusy) {
                    return;
                }
                this.isLoading = true;
                this.hasError = false;
                try {
                    const response = await MeetingService.getShareCandidates(this.groupIds);
                    const candidatesById = new Map([...this.modelValue, ...response.data].map((user) => [user.id, user]));
                    this.candidates = [...candidatesById.values()];
                    this.selectedIds = this.modelValue.map((user) => user.id);
                    this.search = "";
                    this.groupFilter = "";
                    this.failedAvatars = {};
                    this.isModalVisible = true;
                } catch (e) {
                    this.hasError = true;
                    if (e.response?.status === 401) {
                        this.$router.push({ name: "Login" });
                    }
                    console.log(e);
                } finally {
                    this.isLoading = false;
                }
            },
            confirmRemove(user) {
                if (this.disabled || this.isBusy) {
                    return;
                }
                this.$store.commit("showConfirmationAlert", () => {
                    if (!this.isBusy) {
                        this.applySelection(this.modelValue.filter((item) => item.id !== user.id));
                    }
                });
            },
            async applySelection(users) {
                if (this.disabled || this.isSaving) {
                    return;
                }
                this.isSaving = true;
                this.hasError = false;
                try {
                    if (this.saveSelection) {
                        await this.saveSelection(users);
                    }
                    this.$emit("update:modelValue", users);
                    await this.$nextTick();
                    this.isModalVisible = false;
                } catch (e) {
                    this.hasError = true;
                    if (e.response?.status === 401) {
                        this.$router.push({ name: "Login" });
                    }
                    console.log(e);
                } finally {
                    this.isSaving = false;
                }
            },
            save() {
                return this.applySelection(this.selectedUsers);
            },
        },
    };
</script>

<style lang="scss" scoped>
    @use "@/assets/scss/esesja-modal.scss";

    .shared-users {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        margin-bottom: 16px;

        &--indented {
            margin-left: 20px;
            margin-right: 20px;
        }

        &__title {
            font-weight: 600;
            font-size: 14px;
            line-height: 145%;
            color: #00082c;
            margin-bottom: 8px;
        }

        &__description {
            overflow-wrap: anywhere;
            font-size: 14px;
            line-height: 145%;
            color: grey;
            margin-bottom: 8px;
        }

        &__tags {
            display: flex;
            flex-wrap: wrap;
            gap: 8px;
            margin-bottom: 12px;
        }

        &__button {
            padding: 12px 16px;
            background: #ffffff;
            border: 1px solid #d1d6df;
            box-shadow: 1px 1px 3px rgba(0, 8, 44, 0.06);
            border-radius: 4px;
            font-size: 14px;
            font-weight: 600;
            line-height: 145%;
            color: #00082c;
            cursor: pointer;

            &:disabled {
                opacity: 0.5;
                cursor: not-allowed;
            }
        }
    }

    .users-picker {
        width: 1000px;
        max-width: 94vw;

        button:disabled {
            opacity: 0.5;
            cursor: not-allowed;
        }

        &__toolbar {
            display: flex;
            gap: 12px;
            margin-bottom: 16px;
        }

        &__search,
        &__group-filter {
            height: 48px;
            background: #ffffff;
            border: 1px solid #d1d6df;
            box-sizing: border-box;
            box-shadow: 1px 1px 3px rgba(0, 8, 44, 0.06);
            border-radius: 4px;
            font-size: 15px;
            color: #00082c;

            &:focus {
                border-color: #00082c;
                outline: none;
            }
        }

        &__search {
            flex: 1;
            padding: 12px 16px;
        }

        &__group-filter {
            width: 300px;
            padding: 0px 12px;
        }

        &__panels {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 16px;

            &--disabled {
                pointer-events: none;
                opacity: 0.6;
            }
        }

        &__panel {
            display: flex;
            flex-direction: column;
            min-width: 0;
            border: 1px solid #e2e5eb;
            border-radius: 8px;
            background: #f7f8fa;
        }

        &__panel-header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 8px;
            padding: 10px 12px;
            border-bottom: 1px solid #e2e5eb;
            font-weight: 600;
            font-size: 15px;
            color: #00082c;
        }

        &__count {
            display: inline-block;
            min-width: 24px;
            margin-left: 6px;
            padding: 1px 8px;
            border-radius: 12px;
            background: #e2e5eb;
            font-size: 13px;
            text-align: center;

            &--selected {
                background: #d80418;
                color: #ffffff;
            }
        }

        &__bulk {
            padding: 6px 10px;
            background: #ffffff;
            border: 1px solid #d1d6df;
            border-radius: 4px;
            font-size: 13px;
            font-weight: 600;
            color: #00082c;
            cursor: pointer;

            &:hover:not(:disabled) {
                background: #e8ebef;
            }

            &:disabled {
                opacity: 0.5;
                cursor: not-allowed;
            }
        }

        &__list {
            display: flex;
            flex-direction: column;
            gap: 6px;
            height: 420px;
            overflow-y: auto;
            padding: 8px;
        }

        &__person {
            display: flex;
            align-items: center;
            gap: 10px;
            padding: 8px 10px;
            background: #ffffff;
            border: 1px solid #e2e5eb;
            border-radius: 6px;

            &--clickable {
                cursor: pointer;
                transition: box-shadow 0.15s ease-in-out;

                &:hover {
                    box-shadow: 0 2px 8px rgba(0, 8, 44, 0.12);
                }
            }
        }

        &__avatar {
            display: flex;
            flex: 0 0 40px;
            align-items: center;
            justify-content: center;
            height: 40px;
            overflow: hidden;
            border-radius: 50%;
            background: #e5e7eb;
            font-size: 14px;
            font-weight: 600;
            color: #333956;

            img {
                width: 100%;
                height: 100%;
                object-fit: cover;
            }
        }

        &__person-data {
            display: flex;
            flex: 1;
            flex-direction: column;
            min-width: 0;
        }

        &__person-name {
            font-size: 15px;
            font-weight: 600;
            color: #00082c;
        }

        &__person-role {
            font-size: 13px;
            color: grey;
        }

        &__person-groups {
            display: flex;
            flex-wrap: wrap;
            gap: 4px;
            margin-top: 4px;
        }

        &__group-chip {
            padding: 1px 7px;
            border-radius: 10px;
            background: #eef1f6;
            font-size: 11px;
            line-height: 150%;
            color: #4b5563;
        }

        &__action {
            flex: 0 0 32px;
            height: 32px;
            border: 1px solid #d1d6df;
            border-radius: 50%;
            background: #ffffff;
            font-size: 20px;
            line-height: 1;
            color: #00082c;
            cursor: pointer;

            &:hover {
                background: #e8ebef;
            }

            &--remove {
                color: #d80418;
            }
        }

        &__empty {
            padding: 24px 8px;
            font-size: 14px;
            text-align: center;
            color: grey;
        }

        /* ten sam margines co nagłówek i treść esesja-modal */
        &__toolbar,
        &__panels,
        &__bottom {
            margin-left: 32px;
            margin-right: 32px;
        }

        &__bottom {
            margin-top: 16px;
            padding-bottom: 32px;
        }

        /* niższe listy na mniejszych ekranach - okno mieści się w widoku bez przewijania całości */
        &__list {
            height: min(420px, 45vh);
        }
    }

    @media (max-width: 760px) {
        .users-picker__toolbar {
            flex-direction: column;
        }

        .users-picker__group-filter {
            width: 100%;
        }

        .users-picker__panels {
            grid-template-columns: 1fr;
        }
    }
</style>
